import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useBulkPublish, useCategories, useCollections, useDeleteProducts, useProducts,
} from '../../api/catalog';
import type { ProductRow } from '../../api/catalog';
import { useSession } from '../../auth/session';
import { RowImage } from './RowImage';
import { Pagination } from '../../ui/Pagination';
import {
  Alert, Button, Chip, Empty, Money, PageHeader, Panel, Spinner, Table, type Column,
} from '../../ui';

/**
 * A-08 — archetype 6: list, filter, row action.
 *
 * **There is no stock column, and there is no availability badge.** Every
 * wholesale order is made to order (D4). The temptation is highest on exactly
 * this screen, because every other catalog tool has one: a buyer looking at a
 * list of products expects a number beside each. The portal is a bridge, not an
 * ERP, and the subtitle says so rather than leaving the absence to be read as an
 * oversight. There is no minimum order quantity either (CR-07).
 *
 * The filters live in component state and go to the server as query parameters
 * rather than filtering a fetched array. A catalog outgrows one page, and a
 * client-side filter over the first page silently searches only that page.
 */
/** Products per page. Small enough to scan, large enough that most catalogs need few pages. */
const PAGE_SIZE = 25;

export function ProductList() {
  const { can } = useSession();
  const bulkPublish = useBulkPublish();
  const deleteProducts = useDeleteProducts();

  /**
   * Selected rows, by product id. Held here rather than in the table because the
   * action bar needs it too, and because it must survive a filter change:
   * narrowing to a collection to find three more products should not silently
   * drop the twelve already chosen.
   */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  /**
   * A-09's Publish lands here and says what it did — the banner arrives in
   * router state, so it starts from that. The state is then dropped from the
   * history entry, or a refresh or a Back into this screen would repeat it.
   */
  const location = useLocation();
  const navigate = useNavigate();
  const arrived = (location.state as { banner?: string } | null)?.banner ?? null;
  const [banner, setBanner] = useState<{ tone: 'them' | 'stop'; text: string } | null>(
    arrived ? { tone: 'them', text: arrived } : null,
  );
  useEffect(() => {
    if (arrived) void navigate('.', { replace: true, state: null });
  }, [arrived, navigate]);

  /** Says its piece, then gets out of the way — a result banner that never
   *  clears itself is still reporting "1 product deleted" long after a
   *  person has moved on to something else. Restarts on every new banner,
   *  so a second action gets its own five seconds rather than inheriting
   *  whatever was left of the first. */
  useEffect(() => {
    if (!banner) return undefined;
    const timer = setTimeout(() => setBanner(null), 5000);
    return () => clearTimeout(timer);
  }, [banner]);

  /**
   * Search, filters and page live in the URL, not in component state: paging
   * keeps them by construction, a refresh keeps them, and Back from a product
   * lands on the same page of the same search instead of the top of everything.
   * Changing a search or a filter drops `page` — page 7 of a different result
   * set is a different place. `replace`, so paging does not fill the history.
   */
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const collectionId = params.get('collection') ?? '';
  const categoryId = params.get('category') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '', 10) || 1);

  const update = (patch: Record<string, string>, keepPage = false) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
      if (!keepPage) next.delete('page');
      return next;
    }, { replace: true });

  /** The box is typed into freely; the search itself follows a beat after typing stops. */
  const [searchText, setSearchText] = useState(search);
  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed === search) return undefined;
    const timer = setTimeout(() => update({ q: trimmed }), 300);
    return () => clearTimeout(timer);
    // `update` is recreated every render and only closes over `setParams`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, search]);

  const { data, isLoading, isPlaceholderData, error } = useProducts({
    search: search || undefined,
    collectionId: collectionId || undefined,
    categoryId: categoryId || undefined,
    page,
    pageSize: PAGE_SIZE,
  });
  const { data: collections } = useCollections();
  const { data: categories } = useCategories();

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Deleting the last product on the last page leaves `page` past the end.
  useEffect(() => {
    if (data && !isPlaceholderData && page > pageCount) update({ page: String(pageCount) }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isPlaceholderData, page, pageCount]);

  /**
   * Names seen on any page so far. A selection survives paging, so a refusal can
   * name a product that is no longer on screen.
   */
  const seenNames = useRef(new Map<string, string>());
  for (const p of rows) seenNames.current.set(p.id, p.name);
  /** A name to put next to a refusal reason — "id" means nothing to a person reading the banner. */
  const nameOf = (id: string) => seenNames.current.get(id) ?? id;

  /** Up to three refusals spelled out by name and reason, not just a count — a
   *  refusal a person cannot read is a refusal they cannot act on. */
  const refusalText = (refused: ReadonlyArray<{ id: string; reason: string }>): string => {
    if (refused.length === 0) return '';
    const shown = refused.slice(0, 3).map((r) => `${nameOf(r.id)} — ${r.reason}`).join('; ');
    return ` ${refused.length} refused: ${shown}`
      + (refused.length > 3 ? `, and ${refused.length - 3} more.` : '.');
  };

  /**
   * Publishing cascades to variants, so the result is reported in both — someone
   * publishing two products wants to know 252 variants went with them. Refusals
   * are partial and counted: a product with no active variant cannot publish,
   * and the rest of the selection should not fail with it — but the reason is
   * spelled out (`refusalText`) rather than left as "open them to see why",
   * which read as a silent failure when every refusal had the same, entirely
   * ordinary cause (D4: nothing to publish with no active variant).
   *
   * `changed === 0` is reported as a refusal (`stop`), not the same neutral
   * tone as a success — the request still comes back 201 (every id was
   * individually refused, which is not the same as the call failing), so
   * without this a fully-refused action read as "0 products published." in
   * the same quiet tone as a real one, indistinguishable from the button
   * having done nothing at all.
   */
  async function publishSelected() {
    if (publishableSelected.length === 0) return;
    setBanner(null);
    try {
      const r = await bulkPublish.mutateAsync({ ids: publishableSelected, isPublished: true });
      setBanner({
        tone: r.changed === 0 ? 'stop' : 'them',
        text: `${r.changed} product${r.changed === 1 ? '' : 's'} published, `
          + `${r.variantsChanged} variant${r.variantsChanged === 1 ? '' : 's'} with them.`
          + refusalText(r.refused),
      });
      setSelected(new Set());
    } catch (e) {
      setBanner({ tone: 'stop', text: e instanceof Error ? e.message : 'That did not work.' });
    }
  }

  /**
   * Withdraw, redefined the same way the Variance block's per-variant
   * Withdraw already was: a genuine removal (`deleteProducts`), not
   * `isPublished: false`. There is no undo — the bulk bar's own "N selected"
   * plus this clearly-labelled button is the same safety net the
   * variant-level Withdraw already relies on, not a second confirmation step
   * this codebase has never used anywhere else.
   */
  async function withdrawSelected() {
    setBanner(null);
    try {
      const r = await deleteProducts.mutateAsync({ ids: [...selected] });
      setBanner({
        tone: r.deleted === 0 ? 'stop' : 'them',
        text: `${r.deleted} product${r.deleted === 1 ? '' : 's'} deleted.`
          + refusalText(r.refused),
      });
      setSelected(new Set());
    } catch (e) {
      setBanner({ tone: 'stop', text: e instanceof Error ? e.message : 'That did not work.' });
    }
  }

  /**
   * "Currently shown" — the header checkbox answers for the filtered page in
   * front of you, not the whole catalog, which is also why it is computed
   * from `rows` rather than kept as state of its own: a filter change or a
   * row leaving the page recomputes it for free, and it can never drift from
   * what `selected` actually holds.
   */
  const selectableIds = rows.map((p) => p.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = !allSelected && selectableIds.some((id) => selected.has(id));
  /**
   * Already-published rows are not something Publish has anything to do —
   * republishing one is a harmless no-op server-side, but showing the
   * button as if it means something for a selection that is already fully
   * live is not. Derived the same way `selectableIds` is (from `rows`,
   * never a snapshot of `selected` itself), so it can never drift from
   * what is actually on screen — Select All included, which reaches this
   * the same way any other selection does.
   */
  const publishableSelected = [...selected].filter((id) => rows.find((p) => p.id === id)?.isPublished === false);

  const columns: Column<ProductRow>[] = [
    ...(can('product.edit') ? [{
      header: (
        <input
          type="checkbox"
          className="mk-rowcheck"
          checked={allSelected}
          // Neither checked nor unchecked reads correctly once the selection
          // is partial — indeterminate has no prop, only a DOM property, so
          // it is set here rather than left unrepresented.
          ref={(el: HTMLInputElement | null) => { if (el) el.indeterminate = someSelected; }}
          aria-label="Select all shown"
          onChange={() => setSelected(allSelected ? new Set() : new Set(selectableIds))}
        />
      ),
      width: '4%',
      center: true,
      /** Select for a bulk action. Only where one could actually be taken. */
      render: (p: ProductRow) => (
        <input
          type="checkbox"
          className="mk-rowcheck"
          checked={selected.has(p.id)}
          aria-label={`Select ${p.name}`}
          onChange={() => setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
            return next;
          })}
        />
      ),
    } as Column<ProductRow>] : []),
    {
      // The photograph gets its own header-less column so "Product Name" sits over the
      // name, not over the image.
      header: '',
      width: '7%',
      render: (p) => <RowImage url={p.imageUrls?.[0]} label={p.name} />,
    },
    {
      /*
       * Name and metal type. `.mk-plist-name` truncates a long name instead of
       * stretching the column (or the whole table) to fit it; the full name is still
       * there as a hover title.
       */
      header: 'Product Name',
      width: '21%',
      render: (p) => (
        <div className="mk-plist-info">
          <Link to={`/catalog/products/${p.codePrefix}`} className="mk-plist-name" title={p.name}>
            {p.name}
          </Link>
          <div className="mk-hint">{p.metalType ?? 'Metal not set'}</div>
        </div>
      ),
    },
    {
      // The actual creator, read from the product's audit trail; '—' where none was recorded.
      header: 'Created By',
      width: '10%',
      render: (p) => <span className="mk-dim">{p.createdBy ?? '—'}</span>,
    },
    {
      header: 'Code',
      width: '10%',
      // The prefix, not a variant code. Every variant code is built from it, which
      // is why it is fixed once the first variant exists (see the frozen contract).
      render: (p) => <code>{p.codePrefix}</code>,
    },
    {
      header: 'Collection',
      width: '12%',
      render: (p) => <span className="mk-dim">{p.collection ?? '—'}</span>,
    },
    {
      header: 'Category',
      width: '12%',
      render: (p) => <span className="mk-dim">{p.category ?? '—'}</span>,
    },
    {
      header: 'Variants',
      center: true,
      width: '8%',
      render: (p) => (
        p.variantCount === 0
          ? <span className="mk-dim">None yet</span>
          : <span>{p.variantCount}</span>
      ),
    },
    {
      // Centered, not right-aligned — grouped visually with Variants and
      // Status rather than pulled to the far edge the way a right-aligned
      // figure would be.
      header: 'Wholesale',
      center: true,
      width: '14%',
      // A product has no one price: 18k is denser than 14k and a bigger stone needs
      // a heavier setting, so the price is per variant. This is the lowest of them,
      // and it stays a decimal string the whole way here (NF-04). `trimZeros`
      // is display-only — the stored value and what's sent on save are
      // untouched, this only decides whether ".00" is worth printing.
      render: (p) => <Money amount={p.priceFrom} currency={p.currency} trimZeros />,
    },
    {
      /* PR-02 — both prices are visible, and a single "Price" column could not
         carry two figures. Lowest RP across the product's active variants, the
         same shape as the wholesale column beside it. */
      header: 'RP',
      center: true,
      width: '14%',
      render: (p) => <Money amount={p.rpFrom} currency={p.currency} trimZeros />,
    },
    {
      header: 'Status',
      center: true,
      width: '12%',
      render: (p) => (
        p.isPublished
          ? <Chip tone="done">Published</Chip>
          : <Chip tone="lapse">Draft</Chip>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Products"
        actions={(
          <>
            {/*
              * Bulk upload belongs here rather than in the navigation: loading a catalogue from
              * a spreadsheet is a way of ADDING PRODUCTS, and it was a top-level entry beside
              * the screen it fills. Its own permission still governs it — `product.bulk` is a
              * different act from amending one product — so somebody who may edit but not bulk
              * load sees one button, not a refusal.
              */}
            {can('product.bulk')
              ? <Link className="mk-btn mk-btn-s mk-btn-g" to="/catalog/bulk">Bulk upload</Link>
              : null}
            {can('product.edit')
              ? <Link className="mk-btn mk-btn-s" to="/catalog/products/new">Add product</Link>
              : null}
          </>
        )}
      />

      {banner ? <Alert tone={banner.tone}>{banner.text}</Alert> : null}

      {/* Only when something is selected, and it says how many. A bulk action
          that does not state its scope before you press it is how twenty
          products get withdrawn instead of two. */}
      {selected.size > 0 ? (
        <div className="mk-bulkbar">
          <b>{selected.size} selected</b>
          <button type="button" className="mk-linkish" onClick={() => setSelected(new Set())}>Clear</button>
          <span className="mk-spacer" />
          {/* Not just disabled — absent. A selection that is already fully
              published has nothing for Publish to do, and a button sitting
              there greyed out for a reason the screen never states reads as
              broken rather than as "nothing to do here". */}
          {publishableSelected.length > 0 ? (
            <Button size="sm" disabled={bulkPublish.isPending} onClick={() => void publishSelected()}>
              Publish{publishableSelected.length < selected.size ? ` (${publishableSelected.length})` : ''}
            </Button>
          ) : null}
          <Button
            size="sm"
            tone="danger"
            disabled={deleteProducts.isPending}
            title="Deletes the selected products completely — this cannot be undone."
            onClick={() => void withdrawSelected()}
          >
            Withdraw
          </Button>
        </div>
      ) : null}

      <div className="mk-toolbar">
        <input
          className="mk-fld mk-toolbar-search"
          value={searchText}
          placeholder="Search code or name"
          aria-label="Search code or name"
          onChange={(e) => setSearchText(e.target.value)}
        />
        <select
          value={collectionId}
          aria-label="Collection"
          onChange={(e) => update({ collection: e.target.value })}
        >
          <option value="">All collections</option>
          {(collections ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={categoryId}
          aria-label="Category"
          onChange={(e) => update({ category: e.target.value })}
        >
          <option value="">All categories</option>
          {(categories ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {error ? <Alert tone="stop">{error.message}</Alert> : null}

      {isLoading ? <Spinner /> : (
        // Dimmed only while a NEW page is in flight (the old rows are still
        // showing), which is the one moment the dim explains itself.
        <Panel>
          <div style={{ opacity: isPlaceholderData ? 0.6 : 1, transition: 'opacity 120ms' }}>
          <Table
            rows={rows}
            columns={columns}
            rowKey={(p) => p.id}
            filterLabel="Filter by name, code or collection"
            filter={(p) => [p.name, p.codePrefix, p.collection, p.category].filter(Boolean).join(' ')}
            dense={false}
            // `table-layout: fixed`, scoped to this table — makes the
            // percentages on `columns` above authoritative instead of the
            // minimums auto-layout treated them as (see the rule's own
            // comment in styles.css for why that was the actual cause of
            // the spacing).
            className="mk-plist-t"
            // The existing selected-row treatment (`.mk-t tr.on`) — a brand
            // wash and a left accent bar, not a border or a colour of its own.
            rowClassName={(p) => (selected.has(p.id) ? 'on' : undefined)}
            empty={
              <Empty
                title="Nothing matches"
                body="No product in this portal answers to that search and those filters."
              />
            }
          />
          </div>
          {total > 0 ? (
            <Pagination
              page={Math.min(page, pageCount)}
              pageSize={PAGE_SIZE}
              total={total}
              onPage={(p) => {
                update({ page: p > 1 ? String(p) : '' }, true);
                window.scrollTo({ top: 0 });
              }}
            />
          ) : null}
        </Panel>
      )}
    </>
  );
}
